import axios, { AxiosError, CanceledError, type InternalAxiosRequestConfig } from 'axios'
import type { AxiosInstance } from 'axios'
import { AUTH_ENDPOINTS } from '@/features/auth/api/auth.endpoints'
import { API_BASE, API_REQUEST_TIMEOUT_MS } from '@/core/api/http'
import {
  authClientHeaders,
  getActiveAccessToken,
  isRecruitmentPortalLoginPath,
  usesPortalAuthClient,
} from '@/core/auth/auth-client-context'
import {
  ensureValidPortalAccessToken,
  redirectToPortalLogin,
  refreshPortalSessionForced,
} from '@/core/auth/portal-session-refresh'
import {
  ensureValidAccessToken,
  isGuestAuthRoute,
  isAccessTokenExpired,
  redirectToLogin,
  refreshSessionForced,
} from '@/core/auth/session-refresh'
import { isHardRefreshFailure } from '@/core/auth/refresh-failure'
import { getApiLocaleHeaders } from '@/core/api/locale-headers'
import { installInFlightGetDedupe } from '@/core/api/in-flight-get-dedupe'
import { isDeviceOnline } from '@/shared/composables/use-network-status'

export const http: AxiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: API_REQUEST_TIMEOUT_MS,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
})

installInFlightGetDedupe(http)

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean; _skipTokenRefresh?: boolean }

/** Endpoints that must not trigger proactive refresh or 401-retry (public auth flow). */
function isPublicAuthEndpoint(url: string): boolean {
  return (
    url.includes(AUTH_ENDPOINTS.LOGIN)
    || url.includes(AUTH_ENDPOINTS.TWO_FACTOR)
    || url.includes(AUTH_ENDPOINTS.REFRESH)
    || url.includes(AUTH_ENDPOINTS.LOGOUT)
    || url.includes(AUTH_ENDPOINTS.FORGOT_PASSWORD)
    || url.includes(AUTH_ENDPOINTS.RESET_PASSWORD)
    || url.includes(AUTH_ENDPOINTS.SETUP_PASSWORD)
  )
}

http.interceptors.request.use(async (config) => {
  if (!isDeviceOnline()) {
    return Promise.reject(
      new AxiosError('Network Error', 'ERR_NETWORK', config),
    )
  }

  const onPortalClient = usesPortalAuthClient()
  const url = config.url ?? ''
  const retriable = config as RetriableConfig
  if (!isPublicAuthEndpoint(url) && !retriable._skipTokenRefresh) {
    try {
      if (onPortalClient) {
        await ensureValidPortalAccessToken()
      } else {
        await ensureValidAccessToken()
      }
    } catch (error) {
      if (isHardRefreshFailure(error)) {
        if (onPortalClient) {
          redirectToPortalLogin()
        } else {
          redirectToLogin()
        }
        return Promise.reject(new CanceledError('Session expired'))
      }
      // Transient refresh failure: continue if access token still usable.
      if (onPortalClient) {
        if (!getActiveAccessToken()) {
          return Promise.reject(error)
        }
      } else if (isAccessTokenExpired() || !getActiveAccessToken()) {
        return Promise.reject(error)
      }
    }
  }

  const token = getActiveAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  Object.assign(config.headers, authClientHeaders())

  // Platform / Workspace admin may send validated switch headers (server re-checks).
  // Skip tenant switch headers on a portal-only PAT — that session is company-bound.
  if (!onPortalClient) {
    const canSwitchTenant = localStorage.getItem('hr_ast_can_switch_tenant') === '1'
    const tenantId = localStorage.getItem('hr_ast_active_tenant_id')
    const workspaceId = localStorage.getItem('hr_ast_active_workspace_id')
    if (canSwitchTenant && tenantId) {
      config.headers['X-Tenant-Id'] = tenantId
    } else if (canSwitchTenant && workspaceId) {
      config.headers['X-Workspace-Id'] = workspaceId
    }
  }

  Object.assign(config.headers, getApiLocaleHeaders())
  if ((config.method ?? 'get').toLowerCase() === 'get') {
    config.headers['Cache-Control'] = 'no-cache'
    config.headers.Pragma = 'no-cache'
    config.headers.Expires = '0'
  }
  // Let the browser set multipart boundary — never keep application/json or bare multipart/form-data.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    const headers = config.headers as { delete?: (name: string) => void } & Record<string, unknown>
    if (typeof headers.delete === 'function') {
      headers.delete('Content-Type')
      headers.delete('content-type')
    } else {
      delete headers['Content-Type']
      delete headers['content-type']
    }
  }
  return config
})

http.interceptors.response.use(
  (res) => res,
  async (err: AxiosError) => {
    const config = err.config as RetriableConfig | undefined
    const status = err.response?.status
    const url = config?.url ?? ''

    const onPortalClient = usesPortalAuthClient()

    if (status === 401 && url.includes(AUTH_ENDPOINTS.REFRESH)) {
      if (onPortalClient) {
        redirectToPortalLogin()
      } else {
        redirectToLogin()
      }
      return Promise.reject(err)
    }

    if (
      status !== 401
      || !config
      || config._retry
      || isPublicAuthEndpoint(url)
      || isGuestAuthRoute()
      || isRecruitmentPortalLoginPath()
    ) {
      return Promise.reject(err)
    }

    config._retry = true
    config._skipTokenRefresh = true

    try {
      if (onPortalClient) {
        await refreshPortalSessionForced()
      } else {
        await refreshSessionForced()
      }

      const token = getActiveAccessToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      Object.assign(config.headers, authClientHeaders())
      return http(config)
    } catch (error) {
      if (isHardRefreshFailure(error)) {
        if (onPortalClient) {
          redirectToPortalLogin()
        } else {
          redirectToLogin()
        }
      }
      return Promise.reject(err)
    }
  },
)
